import React, { useState, useRef } from 'react';
import { X, Upload, FileText, FileSpreadsheet, File, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Theme } from '../types';
import { fetchDefinition } from '../services/dictionary';

interface ImportModalProps {
  onImport: (data: string) => void;
  onClose: () => void;
  theme?: Theme;
}

function ImportModal({ onImport, onClose, theme = 'light' }: ImportModalProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const cleanWord = (word: string): { word: string; originalForm: string } => {
    const original = word;
    
    // Remove leading numbers and dots
    let cleaned = word.replace(/^\d+\.\s*/, '');
    
    // Handle phrases with descriptions or definitions
    if (cleaned.includes('-')) {
      cleaned = cleaned.split('-')[0];
    } else if (cleaned.includes(':')) {
      cleaned = cleaned.split(':')[0];
    }
    
    // Remove trailing punctuation and whitespace
    cleaned = cleaned.replace(/[.,;:!?]$/, '').trim();
    
    // Handle special cases
    if (cleaned.toLowerCase().endsWith('ing')) {
      // Remove 'ing' to get base word for better dictionary lookup
      cleaned = cleaned.slice(0, -3);
    }
    
    return {
      word: cleaned.toLowerCase(),
      originalForm: original.trim()
    };
  };

  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  const processWords = async (words: string[]): Promise<any[]> => {
    const uniqueWords = new Map();
    
    // First pass: clean and deduplicate words
    words.forEach(word => {
      const { word: cleaned, originalForm } = cleanWord(word);
      if (cleaned.length >= 2 && !/^\d+$/.test(cleaned)) {
        uniqueWords.set(cleaned, {
          word: cleaned,
          originalForm,
          definition: '',
          source: 'Imported'
        });
      }
    });

    const processedWords = Array.from(uniqueWords.values());
    setProgress({ current: 0, total: processedWords.length });

    // Second pass: fetch definitions
    const enrichedWords = [];
    for (const wordObj of processedWords) {
      try {
        await delay(1000); // Rate limiting
        const result = await fetchDefinition(wordObj.word);
        
        enrichedWords.push({
          word: wordObj.word,
          originalForm: wordObj.originalForm,
          definition: result.definition,
          source: 'Dictionary API',
          learnerDefinition: result.learners,
          intermediateDefinition: result.intermediate,
          synonyms: result.thesaurus?.synonyms || [],
          antonyms: result.thesaurus?.antonyms || [],
          date: new Date().toISOString(),
          mastery: 0,
          reviewCount: 0
        });

        setProgress(prev => ({ ...prev, current: prev.current + 1 }));
      } catch (error) {
        // Store for manual entry with original form
        enrichedWords.push({
          word: wordObj.word,
          originalForm: wordObj.originalForm,
          definition: `Manual entry needed (Original: ${wordObj.originalForm})`,
          source: 'Manual Entry Required',
          date: new Date().toISOString(),
          mastery: 0,
          reviewCount: 0
        });
        
        console.error(`Failed to fetch definition for ${wordObj.word}:`, error);
      }
    }

    return enrichedWords;
  };

  const processFile = async (file: File) => {
    setIsProcessing(true);
    try {
      const extension = file.name.split('.').pop()?.toLowerCase();
      const reader = new FileReader();

      reader.onload = async (e) => {
        const content = e.target?.result as string;
        let words: string[] = [];

        switch (extension) {
          case 'txt':
            words = content
              .split('\n')
              .filter(line => line.trim());
            break;

          case 'csv':
            const rows = content.split('\n');
            const headers = rows[0].toLowerCase().split(',');
            const wordIndex = headers.findIndex(h => h.includes('word'));
            if (wordIndex === -1) {
              throw new Error('No word column found in CSV');
            }
            words = rows.slice(1)
              .map(row => row.split(',')[wordIndex])
              .filter(word => word?.trim());
            break;

          case 'pdf':
            // Split on common delimiters and filter out noise
            words = content
              .split(/[\n\r\t\f\v.,;:!?()[\]{}"'\s]+/)
              .filter(word => {
                const cleaned = word.trim();
                return cleaned.length > 2 && 
                       !/^\d+$/.test(cleaned) && 
                       !/^[^a-zA-Z]+$/.test(cleaned);
              });
            break;

          default:
            throw new Error('Unsupported file format');
        }

        const processedWords = await processWords(words);
        
        // Count words needing manual entry
        const manualEntryCount = processedWords.filter(
          w => w.source === 'Manual Entry Required'
        ).length;

        if (processedWords.length === 0) {
          throw new Error('No valid words found in file');
        }

        onImport(JSON.stringify(processedWords));
        
        toast.success(
          `Processed ${processedWords.length} words! ${
            manualEntryCount > 0 
              ? `${manualEntryCount} words need manual definitions.` 
              : ''
          }`,
          { duration: 5000 }
        );
      };

      reader.readAsText(file);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Error processing file');
    } finally {
      setIsProcessing(false);
      setIsDragging(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className={`w-full max-w-md p-6 rounded-lg shadow-xl ${
          theme === 'dark' ? 'bg-gray-800' : 'bg-white'
        }`}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className={`text-xl font-semibold ${
            theme === 'dark' ? 'text-white' : 'text-gray-800'
          }`}>
            Import Words
          </h2>
          <button
            onClick={onClose}
            className={`text-gray-500 hover:text-gray-700 ${
              theme === 'dark' ? 'hover:text-gray-300' : ''
            }`}
          >
            <X size={20} />
          </button>
        </div>

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={async (e) => {
            e.preventDefault();
            const file = e.dataTransfer.files[0];
            if (file) await processFile(file);
          }}
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            isDragging
              ? 'border-purple-500 bg-purple-50'
              : theme === 'dark'
              ? 'border-gray-700 bg-gray-900'
              : 'border-gray-300 bg-gray-50'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".txt,.csv,.pdf"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) processFile(file);
            }}
            className="hidden"
          />

          {isProcessing ? (
            <div className="flex flex-col items-center space-y-4">
              <Loader2 className="animate-spin text-purple-500" size={40} />
              <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
                Processing your file...
                {progress.total > 0 && (
                  <span className="block text-sm mt-2">
                    Fetching definitions: {progress.current}/{progress.total}
                  </span>
                )}
              </p>
            </div>
          ) : (
            <>
              <Upload
                className={`mx-auto mb-4 ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}
                size={40}
              />
              <p className={`mb-4 font-medium ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Drag and drop your file here or
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="text-purple-500 hover:text-purple-600 ml-1"
                >
                  browse
                </button>
              </p>

              <div className="flex justify-center space-x-4">
                <div className="flex items-center">
                  <FileText className="text-blue-500 mr-1" size={16} />
                  <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                    .txt
                  </span>
                </div>
                <div className="flex items-center">
                  <FileSpreadsheet className="text-green-500 mr-1" size={16} />
                  <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                    .csv
                  </span>
                </div>
                <div className="flex items-center">
                  <File className="text-red-500 mr-1" size={16} />
                  <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                    .pdf
                  </span>
                </div>
              </div>
            </>
          )}
        </div>

        <div className={`mt-4 text-sm ${
          theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
        }`}>
          <h3 className="font-medium mb-2">File Format Guidelines:</h3>
          <ul className="list-disc list-inside space-y-1">
            <li>TXT: One word/phrase per line</li>
            <li>CSV: Must include a 'word' column</li>
            <li>PDF: Text will be automatically extracted</li>
          </ul>
        </div>
      </motion.div>
    </div>
  );
}

export default ImportModal;